import { Injectable } from '@angular/core';

/**
 * ID: bh-session-storage-service
 * Name: BH Session Storage Service
 * Description: Service for managing session storage (patient-specific data that auto-isolates per tab/MPage)
 * Version: 1
 *
 * ==============================
 * Change Log
 * ==============================
 * 2025-01-09 - AB - v1: Initial implementation to fix multi-patient storage bleed using SessionStorage
 */

@Injectable({
  providedIn: 'root',
})
export class SessionStorageService {
  constructor() {}

  /**
   * Save data to session storage (isolated per tab/MPage)
   */
  async saveData(key: string, value: any): Promise<void> {
    try {
      const serializedValue = JSON.stringify(value);
      sessionStorage.setItem(key, serializedValue);
      console.log(`SessionStorage: Saved '${key}' to session storage`);
    } catch (error) {
      console.error(`SessionStorage: Error saving '${key}':`, error);
      throw error;
    }
  }

  /**
   * Get data from session storage
   */
  async getData<T>(key: string): Promise<T | null> {
    try {
      const item = sessionStorage.getItem(key);
      if (item === null) {
        console.log(`SessionStorage: No data found for key '${key}'`);
        return null;
      }

      const parsedValue = JSON.parse(item);
      console.log(`SessionStorage: Retrieved '${key}' from session storage`);
      return parsedValue;
    } catch (error) {
      console.error(`SessionStorage: Error retrieving '${key}':`, error);
      return null;
    }
  }

  /**
   * Remove data from session storage
   */
  async removeData(key: string): Promise<void> {
    try {
      sessionStorage.removeItem(key);
      console.log(`SessionStorage: Removed '${key}' from session storage`);
    } catch (error) {
      console.error(`SessionStorage: Error removing '${key}':`, error);
      throw error;
    }
  }

  /**
   * Check if a key exists in session storage
   */
  async hasData(key: string): Promise<boolean> {
    return sessionStorage.getItem(key) !== null;
  }

  /**
   * Clear all session storage data
   */
  async clearAll(): Promise<void> {
    try {
      sessionStorage.clear();
      console.log('SessionStorage: Cleared all session storage data');
    } catch (error) {
      console.error('SessionStorage: Error clearing session storage:', error);
      throw error;
    }
  }

  /**
   * Get all keys in session storage (for debugging)
   */
  getAllKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        keys.push(key);
      }
    }
    return keys;
  }

  /**
   * Debug method to log all session storage contents
   */
  debugLogContents(): void {
    console.log('SessionStorage Contents:');
    const keys = this.getAllKeys();
    keys.forEach((key) => {
      const value = sessionStorage.getItem(key);
      console.log(`  ${key}:`, value);
    });
  }
}
